import React from 'react';
import { useDeals } from '../context/DealsContext';
import { FiClock, FiCalendar, FiUser, FiDollarSign } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const RecentDeals = () => {
  const { deals } = useDeals();
  const recentDeals = deals
    .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity))
    .slice(0, 5);

  const getQualityColor = (score) => {
    if (score >= 8) return 'text-green-700 dark:text-green-500';
    if (score >= 6) return 'text-amber-700 dark:text-amber-500';
    return 'text-red-700 dark:text-red-500';
  };

  // Check if a deal has all three strength factors
  const isDealStrong = (deal) => {
    return (
      (deal.purchaserName && deal.purchaserContact) &&
      deal.projectedCloseDate
    );
  };

  // Format date as MM/DD/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="flex items-center space-x-2 mb-4">
        <SafeIcon icon={FiClock} className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
      </div>

      <div className="space-y-4">
        {recentDeals.map((deal) => (
          <div
            key={deal.id}
            className={`flex flex-col p-4 rounded-lg ${
              isDealStrong(deal)
                ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50'
                : 'bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900 dark:text-white">{deal.company}</h4>
              <span className={`text-sm font-medium ${getQualityColor(deal.qualityScore)}`}>
                {deal.qualityScore}/10
              </span>
            </div>

            <div className="flex items-center justify-between mt-1">
              <p className="text-sm text-gray-600 dark:text-gray-400">${deal.value?.toLocaleString()}</p>
              <p className="text-xs text-gray-600 dark:text-gray-300">
                {formatDate(deal.lastActivity)}
              </p>
            </div>

            {/* Deal strength indicators with improved spacing */}
            <div className="flex flex-wrap gap-2 mt-3">
              {deal.projectedCloseDate && (
                <div className="flex items-center text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full border border-purple-200 dark:border-purple-800/50">
                  <SafeIcon icon={FiCalendar} className="w-3 h-3 mr-1" />
                  <span>{formatDate(deal.projectedCloseDate)}</span>
                </div>
              )}
              {deal.purchaserName && deal.purchaserContact && (
                <div className="flex items-center text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full border border-blue-200 dark:border-blue-800/50 max-w-full overflow-hidden">
                  <SafeIcon icon={FiUser} className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span className="truncate">{deal.purchaserName}</span>
                </div>
              )}
              {deal.purchaserName2 && deal.purchaserContact2 && (
                <div className="flex items-center text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full border border-green-200 dark:border-green-800/50">
                  <SafeIcon icon={FiUser} className="w-3 h-3 mr-1" />
                  <span>Secondary</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {recentDeals.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No recent activity
        </div>
      )}
    </div>
  );
};

export default RecentDeals;